import {NotificationsContext} from "@ui-components/Notifications";
import {useContext, useState} from "react";
import useAsyncError from "@hooks/useAsyncError";
import {useIntl} from "react-intl";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import {storeRemoteSpreadsheetResults} from "@feature/remote-calculation-results"

function isNumeric(str) {
  if (typeof str != "string") return false // we only process strings!
  return !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
         !isNaN(parseFloat(str)) // ...and ensure strings of whitespace fail
}


export const useSpreadsheetResults = () => {

  // context
  const intl = useIntl();
  const {push} = useContext(NotificationsContext);

  // hooks
  const countryApiRequest = useCountryAxiosInstance();
  const throwError = useAsyncError();
  const insertResponseInRow = (row, response, id, input_mapping)=> {
    switch(response.question.input_type) {
      case "number_input":
      case "text_input":
        row[`'${input_mapping[id].sheet}'!${input_mapping[id].range}`] = response.value
        break
      case "select":
        row[`'${input_mapping[id].sheet}'!${input_mapping[id].range}`] = isNumeric(response.option.option_value) ?
          parseFloat(response.option.option_value) : response.option.option_value
        break
      case "multi_select":
        throwError("not implemented yet")
        break
    }
  }

  const buildInputData = (responses, input_mapping) => {
    const input_data = []
    const responsesOutsideRecursiveQuestion = []
    const responsesInsideRecursiveQuestion = []
    for (let [id, spreadsheetRange] of Object.entries(input_mapping)) {
      if (responses.hasOwnProperty(id)) {
        responsesOutsideRecursiveQuestion.push(id)
      } else {
        responsesInsideRecursiveQuestion.push(id)
      }
    }
    let recursiveQuestionResponses = null
    // now we have to find in which recursive question the responsesInsideRecursiveQuestion are
    const recursiveQuestionsResponses = Object.values(responses)
      .filter((response) =>
        response.question.type === 'recursive_question')
    if (responsesInsideRecursiveQuestion.length > 0) {
      const id = responsesInsideRecursiveQuestion[0]
      recursiveQuestionResponses = recursiveQuestionsResponses.find((rec_response) => {
        return rec_response.recursive_question_value?.filter((iteration) => {
          return Object.keys(iteration).includes(id)
        }).length > 0
      })
    }
    recursiveQuestionResponses?.recursive_question_value?.filter(i => Object.keys(i).length > 0)
      .forEach((recursiveQuestionResponse) => {
        const row = {}
        for (const id of responsesInsideRecursiveQuestion) {
          const response = recursiveQuestionResponse[id]
          insertResponseInRow(row, response, id, input_mapping)
        }
        for (const id of responsesOutsideRecursiveQuestion) {
          const response = responses[id]
          insertResponseInRow(row, response, id, input_mapping)
        }
        input_data.push(row)
      })
    return input_data
  }
  // state
  const [loading, setLoading] = useState(false);

  // handlers
  const recompute = async (
    responses: { [key: string]: any },
    surveyId: number,
    output: any,
    spreadsheetValues: any,
  ) => {
    setLoading(true);
    // build the payload
    const spreadSheetConfiguration = JSON.parse(output.calculation[0])
    const inputData = buildInputData(responses, spreadSheetConfiguration.input_mapping)
    const basePayload = {
            "survey_id": surveyId,
            "output_id": output.id,
            "spreadsheet_id": spreadSheetConfiguration.spreadsheet_id,
            "spreadsheet_name": spreadSheetConfiguration.spreadsheet_name,
            "output_ranges": spreadSheetConfiguration.output_ranges.map((output_range) => {
              return `'${output_range.sheet}'!${output_range.range}`
            }),
          }
    const formattedResults = []
    /**
     * !!! we need to post each row separately because if there are
     * many rows, then the api will risk to timeout !!! (504 after 30s)
     */
    for (let [index, row] of inputData.entries()){
      const rowPayload = {...basePayload, input_data: [row]}
      const results = await countryApiRequest!.post("/google_sheets", rowPayload);
      push({title:`${intl.formatMessage({id: "done"})} ${intl.formatMessage({id: "row"})} ${index+1}/${inputData.length}`, type:"success"})
      for (let result of results){
        let formattedResultRow = {}
        for (let [i, row] of Object.entries(result)){
          const key = spreadSheetConfiguration.output_ranges[i].key
          switch (spreadSheetConfiguration.output_ranges[i].type) {
            case "number":
              formattedResultRow[key] = parseFloat(row.values[0][0].replace(",", "."))
              break
            case "text":
              formattedResultRow[key] = row.values[0][0]
              break
            default:
              throwError("not implemented yet")
          }
        }
        formattedResults.push(formattedResultRow)
      }
    }
    // store the results in the remote spreadsheet cache as if they were computed all at once
    const result = {...spreadsheetValues, [output.id]:formattedResults}
    storeRemoteSpreadsheetResults(result, {...basePayload, input_data:inputData}, countryApiRequest!);
    setLoading(false)
    return result;
  }

  return {recompute, loading};
}